import {
  KoButton,
  KoDrawer,
  KoForm,
  KoLoader,
  KoModal,
  KoTable,
  KoText,
  KoTitle,
  KoTag,
  KoToast,
  KoTextArea,
  KoEmpty,
  KoPagination
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import {
  COLUMNS_INCIDENTS,
  INCIDENT_SEVERITY_CONFIG,
  INCIDENT_STATUS_CONFIG
} from '../constants';
import IncidentCrud from './crud/IncidentCrud';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_INCIDENTS = [
  { _id: 'inc-001', incidentId: 'INC-2024-001', title: 'Payment Gateway Timeout', severity: 'sev1', status: 'investigating', service: 'payment-processor', owner: 'John Doe', createdAt: '2026-02-24T08:32:00Z', mttr: '1.5h', linkedRelease: 'v4.2.0', affectedUsers: 1200, description: 'Users experiencing 504 timeouts on checkout flow' },
  { _id: 'inc-002', incidentId: 'INC-2024-002', title: 'Search Index Degradation', severity: 'sev2', status: 'mitigated', service: 'search-engine', owner: 'Alice Park', createdAt: '2026-02-24T07:50:00Z', mttr: '2.1h', linkedRelease: 'v4.2.0', affectedUsers: 3500, description: 'Search returning stale results, reindexing in progress' },
  { _id: 'inc-003', incidentId: 'INC-2024-003', title: 'Auth Token Refresh Failure', severity: 'sev2', status: 'resolved', service: 'auth-service', owner: 'Bob Chen', createdAt: '2026-02-23T22:15:00Z', resolvedAt: '2026-02-23T23:45:00Z', mttr: '1.5h', linkedRelease: 'v4.1.3', affectedUsers: 850, description: 'JWT refresh endpoint returning 500 intermittently' },
  { _id: 'inc-004', incidentId: 'INC-2024-004', title: 'Email Delivery Delay', severity: 'sev3', status: 'resolved', service: 'email-service', owner: 'Support Team', createdAt: '2026-02-23T14:00:00Z', resolvedAt: '2026-02-23T16:30:00Z', mttr: '2.5h', linkedRelease: '', affectedUsers: 200, description: 'Transactional emails delayed by 15-30 minutes' },
  { _id: 'inc-005', incidentId: 'INC-2024-005', title: 'Dashboard Widget Loading Failure', severity: 'sev3', status: 'open', service: 'analytics-pipeline', owner: 'DevOps Team', createdAt: '2026-02-24T06:00:00Z', mttr: '', linkedRelease: 'v4.2.0', affectedUsers: 500, description: 'Analytics widgets showing spinner indefinitely' }
];

const MOCK_TIMELINE = [
  { timestamp: '2026-02-24T08:32:00Z', text: 'Incident created — Payment Gateway Timeout detected', author: 'AlertBot' },
  { timestamp: '2026-02-24T08:35:00Z', text: 'Severity escalated to SEV-1. On-call engineer paged.', author: 'System' },
  { timestamp: '2026-02-24T08:42:00Z', text: 'Root cause identified: connection pool exhaustion on payment-processor pods', author: 'John Doe' },
  { timestamp: '2026-02-24T08:50:00Z', text: 'Scaling payment-processor to 8 replicas. Draining stale connections.', author: 'DevOps Bot' },
  { timestamp: '2026-02-24T09:05:00Z', text: 'Error rate dropping. Monitoring for stabilization.', author: 'John Doe' }
];

const MOCK_SERVICE_OPTIONS = [
  { label: 'api-gateway', value: 'svc-001' },
  { label: 'auth-service', value: 'svc-002' },
  { label: 'payment-processor', value: 'svc-003' },
  { label: 'search-engine', value: 'svc-006' },
  { label: 'analytics-pipeline', value: 'svc-008' },
  { label: 'email-service', value: 'svc-010' }
];

const MOCK_RELEASE_OPTIONS = [
  { label: 'v4.2.0 — Platform Upgrade', value: 'REL-001' },
  { label: 'v4.1.3 — Auth Redesign', value: 'REL-002' },
  { label: 'v4.1.4 — Hotfix: Payment Gateway', value: 'REL-005' }
];

function Incidents() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [incidents, setIncidents] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [noteText, setNoteText] = useState('');

  const [serviceList, setServiceList] = useState([]);
  const [releaseList, setReleaseList] = useState([]);
  const [form] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/monitoring`
    : '/monitoring';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring', path: basePath },
      { title: 'Incidents' }
    ]));
    fetchLists();
  }, [workspaceId]);

  useEffect(() => {
    fetchIncidents();
  }, [workspaceId, pagination]);

  const fetchLists = () => {
    MonitoringAPI.getServiceList().then(
      (data) => {
        const list = (data?.data || data || []).map(s => ({ label: s.serviceName || s.name, value: s.serviceId || s._id }));
        setServiceList(list.length ? list : MOCK_SERVICE_OPTIONS);
      },
      () => setServiceList(MOCK_SERVICE_OPTIONS)
    );
    MonitoringAPI.getReleaseList({ limit: 50 }).then(
      (data) => {
        const list = (data?.data || data || []).map(r => ({ label: `${r.version} — ${r.name}`, value: r._id || r.releaseId }));
        setReleaseList(list.length ? list : MOCK_RELEASE_OPTIONS);
      },
      () => setReleaseList(MOCK_RELEASE_OPTIONS)
    );
  };

  const fetchIncidents = () => {
    setLoading(true);
    MonitoringAPI.getIncidents({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_INCIDENTS;
        setIncidents(data);
        setTotalCount(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setIncidents(MOCK_INCIDENTS); setTotalCount(MOCK_INCIDENTS.length); setLoading(false); }
    );
  };

  const handleSave = (values) => {
    setLoading(true);
    MonitoringAPI.createIncident(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setModalOpen(false);
        form.resetFields();
        setCurrentRecord(null);
        fetchIncidents();
        KoToast.success(currentRecord ? 'Incident updated.' : 'Incident reported.');
      },
      () => { setLoading(false); KoToast.error('Failed to save incident.'); }
    );
  };

  const handleDelete = () => {
    setLoading(true);
    MonitoringAPI.deleteIncident(currentRecord._id || currentRecord.incidentId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchIncidents();
        KoToast.success('Incident deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const openDetail = (record) => {
    setSelectedIncident(record);
    setDrawerOpen(true);
    MonitoringAPI.getIncidentTimeline(record._id || record.incidentId).then(
      (data) => { const events = data?.events || data || []; setTimeline(events.length ? events : MOCK_TIMELINE); },
      () => setTimeline(MOCK_TIMELINE)
    );
  };

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    MonitoringAPI.addIncidentNote(selectedIncident._id || selectedIncident.incidentId, { text: noteText }).then(
      () => {
        setNoteText('');
        KoToast.success('Note added.');
        MonitoringAPI.getIncidentTimeline(selectedIncident._id || selectedIncident.incidentId).then(
          (data) => setTimeline(data?.events || data || []),
          () => {}
        );
      },
      () => KoToast.error('Failed to add note.')
    );
  };

  const summary = {
    open: incidents.filter(i => i.status === 'open').length,
    critical: incidents.filter(i => i.severity === 'sev1' && i.status !== 'resolved').length,
    thisWeek: incidents.length,
    avgMttr: incidents.filter(i => i.mttr).reduce((a, i) => a + (parseFloat(i.mttr) || 0), 0) / (incidents.filter(i => i.mttr).length || 1)
  };

  const columnsWithActions = [
    ...COLUMNS_INCIDENTS,
    {
      label: '', id: 'actions', key: 'actions', width: 120,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EyeOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); openDetail(record); }}
          />
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); form.setFieldsValue(record); setModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='incidents' />

      <KoToolBar
        title='Incidents'
        search={{ visible: true, placeholder: 'Search incidents...', onChange: () => {} }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'Report Incident',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); form.resetFields(); setModalOpen(true); }
        }]}
      />

      {/* Summary Stats */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(245, 34, 45, 0.1)' }}>
            <KoIcon name='AlertOutlined' width={22} height={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{summary.open}</span>
            <span className={styles.statLabel}>Open Incidents</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(250, 173, 20, 0.1)' }}>
            <KoIcon name='ClockCircleOutlined' width={22} height={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{summary.avgMttr.toFixed(1)}h</span>
            <span className={styles.statLabel}>Avg MTTR</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(114, 46, 209, 0.1)' }}>
            <KoIcon name='ThunderboltOutlined' width={22} height={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{summary.thisWeek}</span>
            <span className={styles.statLabel}>This Period</span>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon} style={{ background: 'rgba(245, 34, 45, 0.1)' }}>
            <KoIcon name='CloseCircleOutlined' width={22} height={22} />
          </div>
          <div className={styles.statInfo}>
            <span className={styles.statValue}>{summary.critical}</span>
            <span className={styles.statLabel}>Critical Open</span>
          </div>
        </div>
      </div>

      {incidents.length === 0 && !isLoading && <KoEmpty description='No incidents found' />}
      {incidents.length > 0 && (
        <>
          <KoTable maxHeight={440} dataSource={incidents} columns={columnsWithActions} rowKey={(r) => r._id || r.incidentId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      {/* Incident Detail Drawer */}
      <KoDrawer
        title='Incident Detail'
        open={isDrawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedIncident(null); setTimeline([]); setNoteText(''); }}
        width={'min(700px, 95vw)'}
      >
        {selectedIncident && (
          <div>
            <div className={styles.incidentDrawerHeader}>
              <KoTitle level={4} text={selectedIncident.title} />
              <KoTag color={INCIDENT_SEVERITY_CONFIG[selectedIncident.severity]?.color}>
                {INCIDENT_SEVERITY_CONFIG[selectedIncident.severity]?.label}
              </KoTag>
              <KoTag color={INCIDENT_STATUS_CONFIG[selectedIncident.status]?.color}>
                {INCIDENT_STATUS_CONFIG[selectedIncident.status]?.label}
              </KoTag>
              {selectedIncident.mttr && (
                <span className={styles.mttrBadge}>
                  <KoIcon name='ClockCircleOutlined' width={13} height={13} />
                  MTTR: {selectedIncident.mttr}
                </span>
              )}
            </div>

            <div className={styles.logMetaGrid}>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Owner</span>
                <span className={styles.logDetailValue}>{selectedIncident.owner || '—'}</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Service</span>
                <span className={styles.logDetailValue}>{selectedIncident.service || '—'}</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Environment</span>
                <span className={styles.logDetailValue}>{selectedIncident.environment || '—'}</span>
              </div>
              <div className={styles.logMetaItem}>
                <span className={styles.logDetailLabel}>Linked Release</span>
                <span className={styles.logDetailValue}>{selectedIncident.linkedRelease || '—'}</span>
              </div>
            </div>

            {selectedIncident.description && (
              <div className={styles.logDetailSection}>
                <div className={styles.logDetailLabel}>Description</div>
                <div className={styles.resolutionNotes}>{selectedIncident.description}</div>
              </div>
            )}

            {selectedIncident.rootCause && (
              <div className={styles.logDetailSection}>
                <div className={styles.logDetailLabel}>Root Cause</div>
                <div className={styles.resolutionNotes}>{selectedIncident.rootCause}</div>
              </div>
            )}

            {selectedIncident.resolutionNotes && (
              <div className={styles.logDetailSection}>
                <div className={styles.logDetailLabel}>Resolution Notes</div>
                <div className={styles.resolutionNotes}>{selectedIncident.resolutionNotes}</div>
              </div>
            )}

            {/* Timeline */}
            <div className={styles.noteSectionWrap}>
              <div className={styles.logDetailLabel}>Timeline</div>
              {timeline.length === 0 && <KoText text='No timeline events yet.' />}
              <div className={styles.incidentTimeline}>
                {timeline.map((event, i) => (
                  <div key={i} className={styles.timelineItem}>
                    <div className={styles.timelineTime}>{event.timestamp}</div>
                    <div className={styles.timelineContent}>{event.text || event.message}</div>
                    {event.author && <div className={styles.timelineAuthor}>— {event.author}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Add Note */}
            <div className={styles.noteSectionWrap}>
              <div className={styles.logDetailLabel}>Add Note</div>
              <div className={styles.noteInputRow}>
                <KoTextArea
                  rows={2}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder='Add a timeline note...'
                  style={{ flex: 1 }}
                />
                <KoButton type='primary' label='Add' onClick={handleAddNote} />
              </div>
            </div>
          </div>
        )}
      </KoDrawer>

      {/* Create/Edit Modal */}
      <KoModal
        title={currentRecord ? 'Edit Incident' : 'Report Incident'}
        centered
        open={isModalOpen}
        handleOk={() => setModalOpen(false)}
        onClose={() => { setModalOpen(false); setCurrentRecord(null); }}
        width={'min(740px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => form.submit()} />
        ]}
      >
        <IncidentCrud form={form} handleFinish={handleSave} services={serviceList} releases={releaseList} isEditing={!!currentRecord} />
      </KoModal>

      {/* Delete Confirmation */}
      <KoModal
        title='Delete Incident'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.title}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Incidents;
